import { useUAContext } from '@/context/UAContext'
import { getAdConfig } from '@/lib/advertising'
import { cn } from '@/lib/utils'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

const GOOGLE_AD_DOMAIN = 'pagead2.googlesyndication.com'

/**
 * Google AdSense component
 * Please confirm the component won't be unmounting, because lazy loading script state won't be checked again.
 */
function GoogleAdSense() {
  const adRef = useRef(null)
  const [mounted, setMounted] = useState(false)
  const [adAdded, setAdAdded] = useState(false)
  const { googleAdClientId, googleAdSlotId } = getAdConfig()
  const { isMobile, isMobileLandscape } = useUAContext()
  const styles = useMemo(
    () =>
      isMobile
        ? isMobileLandscape
          ? { display: 'none' }
          : { display: 'flex', justifyContent: 'center' }
        : { display: 'block' },
    [isMobile, isMobileLandscape],
  )
  const format = useMemo(() => (isMobile ? 'rectangle, vertical' : 'horizontal'), [isMobile])

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    const googleAdScript = document.querySelector(`script[src*="${GOOGLE_AD_DOMAIN}"]`)
    if (googleAdScript) {
      return
    }
  }, [])

  useLayoutEffect(() => {
    // Due to customize for responsive if mobile landscape, don't add the ad
    if (!mounted || adAdded || !adRef.current || isMobileLandscape) return
    requestAnimationFrame(() => {
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
      setAdAdded(true)
    })
  }, [adAdded, mounted, isMobileLandscape])

  return (
    <div
      ref={adRef}
      className={cn(
        "[&:has(ins[data-ad-status='unfilled'])]:hidden",
        'w-full',
        isMobileLandscape && 'hidden',
      )}
    >
      {mounted && (
        <ins
          key={format}
          className={cn('adsbygoogle md:h-[90px]')}
          style={styles}
          data-ad-client={googleAdClientId}
          data-ad-slot={googleAdSlotId}
          data-ad-format={format}
          data-full-width-responsive="false"
        />
      )}
    </div>
  )
}

export default GoogleAdSense
